import React, { useMemo } from 'react';
import { SelectiveFilterDateBasedOption } from 'libs/end-to-end/src/selective-filtering/selective-filter-matching-option';
import { SelectiveFilterDateTypes } from 'libs/end-to-end/src/selective-filtering/selective-filter-matching-option';
import { SelectiveFilterStringBasedOption } from 'libs/end-to-end/src/selective-filtering/selective-filter-matching-option';
import { SelectiveFilterStringTypes } from 'libs/end-to-end/src/selective-filtering/selective-filter-matching-option';
import { Spacing } from 'apps/sigma2-ui/src/components/ui/Spacing';
import { SelectBoxWithSearch } from 'apps/sigma2-ui/src/components/ui/SelectBoxWithSearch';
import { guardExhaustive } from 'libs/end-to-end/src/generic';

const SelectiveFilterStringDropdownValues = SelectiveFilterStringTypes.map(
  (type) => ({
    optionValue: type,
    optionLabel: type,
  }),
);

export const SelectiveFilterStringBasedOptionComponent = ({
  option,
  setOption,
}: {
  option: SelectiveFilterStringBasedOption;
  setOption: (option: SelectiveFilterStringBasedOption) => void;
}) => {
  const detailsUi = useMemo(() => {
    switch (option.type) {
      case 'equals':
      case 'contains':
      case 'starts-with':
        return (
          <input
            className="form-input"
            type={'text'}
            value={option.value}
            onChange={(e) => {
              setOption({
                ...option,
                value: e.currentTarget.value,
              });
            }}
          />
        );
      default:
        throw guardExhaustive(option);
    }
  }, [option, setOption]);

  return (
    <Spacing size={'sm'} direction={'vertical'}>
      <SelectBoxWithSearch
        type={'single'}
        setSelected={(value) => {
          if (value === null) {
            return;
          }

          if (typeof value !== 'string') {
            return;
          }

          setOption({
            ...option,
            type: value,
          });
        }}
        selected={option.type}
        list={SelectiveFilterStringDropdownValues}
        label={null}
      />

      {detailsUi}
    </Spacing>
  );
};

export const SelectiveFilterDateDropdownValues = SelectiveFilterDateTypes.map(
  (type) => ({
    optionValue: type,
    optionLabel: type,
  }),
);

export const SelectiveFilterDateBasedOptionComponent = ({
  option,
  setOption,
}: {
  option: SelectiveFilterDateBasedOption;
  setOption: (option: SelectiveFilterDateBasedOption) => void;
}) => {
  const detailsUi = useMemo(() => {
    switch (option.type) {
      case 'date-is-before':
      case 'date-is-after':
        return (
          <input
            className="form-input"
            type={'date'}
            value={option.date}
            onChange={(e) => {
              setOption({
                ...option,
                date: e.currentTarget.value,
              });
            }}
          />
        );
      case 'date-relative-by-days':
        return (
          <input
            className="form-input"
            type={'number'}
            value={option.days}
            onChange={(e) => {
              setOption({
                ...option,
                days: e.currentTarget.valueAsNumber ?? 0,
              });
            }}
          />
        );
      default:
        throw guardExhaustive(option);
    }
  }, [option, setOption]);

  return (
    <Spacing size={'sm'} direction={'vertical'}>
      <SelectBoxWithSearch
        type={'single'}
        setSelected={(value) => {
          if (value === null) {
            return;
          }

          if (typeof value !== 'string') {
            return;
          }

          switch (value) {
            case 'date-is-before':
            case 'date-is-after':
              setOption({
                type: value,
                date: '',
              });
              break;
            case 'date-relative-by-days':
              setOption({
                type: value,
                days: 0,
              });
              break;
            default:
              throw guardExhaustive(value);
          }
        }}
        selected={option.type}
        list={SelectiveFilterDateDropdownValues}
        label={null}
      />

      {detailsUi}
    </Spacing>
  );
};
